import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Field, Input, Stack, useDisclosure } from "@chakra-ui/react";
import { CustomDialog } from "../../../shared/views/CustomDialog";
import { UserNotFoundPrompt } from "../../../shared/views/UserNotFoundPrompt/UserNotFoundPrompt";
import { UserApi } from "../../../account/services/UserApi";
import { isValidEmail } from "../../../shared/utils/validation-utils";
import { AdminAccountApi } from "../../services/AdminAccountsApi";

const AdminCreateAccountModalBase = ({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) => {
  const createUserModal = useDisclosure();

  const [accountName, setAccountName] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const trimmedEmail = emailInput.trim();

  const emailValid = isValidEmail(trimmedEmail);

  const canSubmit = accountName.trim().length > 0 && emailValid;

  const addAccount = async () => {
    const userExists = await UserApi.exists(trimmedEmail).then((r) => r.data);

    if (userExists) {
      await AdminAccountApi.createAccount({
        name: accountName.trim(),
        adminEmail: trimmedEmail,
      });
      onCreated();
      onClose();
    } else {
      createUserModal.onOpen();
    }
  };

  return (
    <>
      <CustomDialog
        onClose={onClose}
        size="lg"
        header="Create new account"
        footer={
          <Button
            flexShrink={0}
            colorPalette="primary"
            onClick={addAccount}
            disabled={!canSubmit}
          >
            Submit
          </Button>
        }
      >
        <Stack gap={4}>
          <Field.Root>
            <Field.Label>Account name</Field.Label>
            <Input
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
            />
          </Field.Root>

          <Field.Root invalid={!emailValid && emailInput.length > 0}>
            <Field.Label>Admin user email</Field.Label>
            <Input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              autoComplete="off"
            />
            <Field.ErrorText>Invalid email</Field.ErrorText>
          </Field.Root>
        </Stack>
      </CustomDialog>

      {createUserModal.open && (
        <UserNotFoundPrompt
          onClose={createUserModal.onClose}
          email={trimmedEmail}
          onConfirm={addAccount}
        />
      )}
    </>
  );
};

export const AdminCreateAccountModal = observer(AdminCreateAccountModalBase);
